/**
 * Sistema de manejo de errores para la aplicación.
 * Tipado completo para errores de API y normalización de errores.
 */

/**
 * Estructura de respuesta de error del backend
 */
export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    status: number;
    path: string;
    timestamp: string;
  };
}

/**
 * Clase de error personalizada para errores de API.
 * Normaliza todos los errores a un formato consistente.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, any>;
  public readonly path: string;
  public readonly timestamp: string;

  constructor(errorResponse: ApiErrorResponse['error']) {
    super(errorResponse.message);
    this.name = 'ApiError';
    this.code = errorResponse.code;
    this.status = errorResponse.status;
    this.details = errorResponse.details;
    this.path = errorResponse.path;
    this.timestamp = errorResponse.timestamp;
  }

  /**
   * Verifica si el error coincide con un código específico
   */
  is(errorCode: string): boolean {
    return this.code === errorCode;
  }

  /**
   * Verifica si el error coincide con alguno de los códigos proporcionados
   */
  isOneOf(...errorCodes: string[]): boolean {
    return errorCodes.includes(this.code);
  }

  /**
   * Serializa el error para logging
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      status: this.status,
      details: this.details,
      path: this.path,
      timestamp: this.timestamp,
    };
  }
}

/**
 * Códigos de error conocidos del backend
 * Mantener sincronizado con el backend
 */
export const ERROR_CODES = {
  // Auth
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_INVALID_TOKEN: 'AUTH_INVALID_TOKEN',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_MISSING_TOKEN: 'AUTH_MISSING_TOKEN',
  AUTH_USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',
  AUTH_USER_ALREADY_EXISTS: 'AUTH_USER_ALREADY_EXISTS',
  AUTH_INACTIVE_USER: 'AUTH_INACTIVE_USER',
  AUTH_INSUFFICIENT_PERMISSIONS: 'AUTH_INSUFFICIENT_PERMISSIONS',
  AUTH_VALIDATION_ERROR: 'AUTH_VALIDATION_ERROR',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',

  // Templates
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  TEMPLATE_VALIDATION_ERROR: 'TEMPLATE_VALIDATION_ERROR',
  TEMPLATE_RETRIEVAL_ERROR: 'TEMPLATE_RETRIEVAL_ERROR',
  TEMPLATE_ALREADY_EXISTS: 'TEMPLATE_ALREADY_EXISTS',
  TEMPLATE_NAME_DUPLICATE: 'TEMPLATE_NAME_DUPLICATE',
  TEMPLATE_NO_TEMPLATES_FOUND: 'TEMPLATE_NO_TEMPLATES_FOUND',
  TEMPLATE_INVALID_FORMAT: 'TEMPLATE_INVALID_FORMAT',
  TEMPLATE_ACCESS_DENIED: 'TEMPLATE_ACCESS_DENIED',
  BLOCK_LIMIT_REACHED: 'BLOCK_LIMIT_REACHED',

  // Reports
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  REPORT_VALIDATION_ERROR: 'REPORT_VALIDATION_ERROR',
  REPORT_GENERATION_ERROR: 'REPORT_GENERATION_ERROR',
  REPORT_SAVE_ERROR: 'REPORT_SAVE_ERROR',
  REPORT_ALREADY_EXISTS: 'REPORT_ALREADY_EXISTS',
  REPORT_RETRIEVAL_ERROR: 'REPORT_RETRIEVAL_ERROR',
  REPORT_MODALITY_NOT_FOUND: 'REPORT_MODALITY_NOT_FOUND',
  REPORT_ACCESS_DENIED: 'REPORT_ACCESS_DENIED',
  REPORT_DATA_INVALID: 'REPORT_DATA_INVALID',
  REPORT_MISSING_FIELD: 'REPORT_MISSING_FIELD',
  REPORT_INVALID_FORMAT: 'REPORT_INVALID_FORMAT',
  REPORT_INVALID_GENERATION_REQUEST: 'REPORT_INVALID_GENERATION_REQUEST',
  REPORT_API_ERROR: 'REPORT_API_ERROR',
  REPORT_SERVICE_ERROR: 'REPORT_SERVICE_ERROR',
  REPORT_ALIGNMENT_ERROR: 'REPORT_ALIGNMENT_ERROR',
  REPORT_NO_MODALITY_OR_REGIONS: 'REPORT_NO_MODALITY_OR_REGIONS',
  REPORT_TEMPLATES_FOUND_FOR_DIFFERENT_TYPE: 'REPORT_TEMPLATES_FOUND_FOR_DIFFERENT_TYPE',

  // Reports - Python API (forwarded codes)
  REPORTS_TEMPLATE_NOT_FOUND: 'REPORTS_TEMPLATE_NOT_FOUND',
  REPORTS_TEMPLATE_INCOMPLETE: 'REPORTS_TEMPLATE_INCOMPLETE',
  REPORTS_TEMPLATE_CONTENT_NOT_FOUND: 'REPORTS_TEMPLATE_CONTENT_NOT_FOUND',
  REPORTS_TEMPLATE_CONTENT_INCOMPLETE: 'REPORTS_TEMPLATE_CONTENT_INCOMPLETE',
  REPORTS_PROMPT_NOT_FOUND: 'REPORTS_PROMPT_NOT_FOUND',
  REPORTS_FINDINGS_GENERATION_ERROR: 'REPORTS_FINDINGS_GENERATION_ERROR',
  REPORTS_CONCLUSION_GENERATION_ERROR: 'REPORTS_CONCLUSION_GENERATION_ERROR',
  REPORTS_SECTION_GENERATION_ERROR: 'REPORTS_SECTION_GENERATION_ERROR',
  REPORTS_PARALLEL_GENERATION_ERROR: 'REPORTS_PARALLEL_GENERATION_ERROR',
  REPORTS_INVALID_GENERATION_PARAMETERS: 'REPORTS_INVALID_GENERATION_PARAMETERS',
  REPORTS_ALIGNMENT_ERROR: 'REPORTS_ALIGNMENT_ERROR',
  REPORTS_PRE_SAVE_REVIEW_ERROR: 'REPORTS_PRE_SAVE_REVIEW_ERROR',
  REPORTS_LLM_SERVICE_ERROR: 'REPORTS_LLM_SERVICE_ERROR',
  REPORT_LANGUAGE_CODE_NOT_FOUND: 'REPORT_LANGUAGE_CODE_NOT_FOUND',
  REPORT_INACTIVE_TEMPLATE: 'REPORT_INACTIVE_TEMPLATE',
  REPORT_ANATOMICAL_REGION_NOT_FOUND: 'REPORT_ANATOMICAL_REGION_NOT_FOUND',
  REPORT_VARIANT_NOT_FOUND: 'REPORT_VARIANT_NOT_FOUND',
  REPORT_TEMPLATE_NOT_FOUND: 'REPORT_TEMPLATE_NOT_FOUND',

  // Ainovis
  AINOVIS_API_ERROR: 'AINOVIS_API_ERROR',
  AINOVIS_CONNECTION_ERROR: 'AINOVIS_CONNECTION_ERROR',
  AINOVIS_TIMEOUT_ERROR: 'AINOVIS_TIMEOUT_ERROR',
  AINOVIS_INVALID_AUDIO_FORMAT: 'AINOVIS_INVALID_AUDIO_FORMAT',
  AINOVIS_AUDIO_FILE_SIZE_EXCEEDED: 'AINOVIS_AUDIO_FILE_SIZE_EXCEEDED',
  AINOVIS_MISSING_AUDIO_FILE: 'AINOVIS_MISSING_AUDIO_FILE',

  // STT (Speech-to-Text)
  STT_SERVICE_ERROR: 'STT_SERVICE_ERROR',
  STT_VALIDATION_ERROR: 'STT_VALIDATION_ERROR',
  STT_MISSING_FIELD: 'STT_MISSING_FIELD',
  STT_PROCESSING_ERROR: 'STT_PROCESSING_ERROR',
  STT_API_ERROR: 'STT_API_ERROR',

  // STT - Python API (forwarded codes)
  STT_NO_VOICE_DETECTED: 'STT_NO_VOICE_DETECTED',
  STT_AUDIO_PROCESSING_ERROR: 'STT_AUDIO_PROCESSING_ERROR',
  STT_NO_AUDIO_CHUNKS: 'STT_NO_AUDIO_CHUNKS',
  STT_TRANSCRIPTION_ERROR: 'STT_TRANSCRIPTION_ERROR',
  STT_NO_TRANSCRIPTION_RESULTS: 'STT_NO_TRANSCRIPTION_RESULTS',
  STT_EMPTY_TRANSCRIPT: 'STT_EMPTY_TRANSCRIPT',
  STT_INVALID_AUDIO_FILE: 'STT_INVALID_AUDIO_FILE',
  STT_AUDIO_FORMAT_NOT_SUPPORTED: 'STT_AUDIO_FORMAT_NOT_SUPPORTED',

  // Email
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
  EMAIL_INVALID_CONFIG: 'EMAIL_INVALID_CONFIG',
  EMAIL_INVALID_ADDRESS: 'EMAIL_INVALID_ADDRESS',
  EMAIL_TEMPLATE_NOT_FOUND: 'EMAIL_TEMPLATE_NOT_FOUND',
  EMAIL_TEMPLATE_RENDER_ERROR: 'EMAIL_TEMPLATE_RENDER_ERROR',
  EMAIL_CONNECTION_ERROR: 'EMAIL_CONNECTION_ERROR',
  EMAIL_AUTHENTICATION_ERROR: 'EMAIL_AUTHENTICATION_ERROR',

  // Network
  NETWORK_ERROR: 'NETWORK_ERROR',
  NO_RESPONSE: 'NO_RESPONSE',
  REQUEST_SETUP_ERROR: 'REQUEST_SETUP_ERROR',
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];
